import { useEffect, useState } from "react";
import { api, formatApiError, formatDate, inr } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";
import { Truck, House, FloppyDisk, Wallet } from "@phosphor-icons/react";
import { CompanyProfileCard, InvoiceSeriesCard } from "@/components/InvoiceSettings";

export default function Settings() {
    const [transporter, setTransporter] = useState("");
    const [rent, setRent] = useState("");
    const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [backfilling, setBackfilling] = useState(false);
    const [lastBackfill, setLastBackfill] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/settings/rcm-rates");
            setTransporter(String(data.transporter ?? ""));
            setRent(String(data.rent ?? ""));
            setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        const t = Number(transporter);
        const r = Number(rent);
        if (Number.isNaN(t) || t < 0 || t > 30) return toast.error("Transporter rate must be 0-30");
        if (Number.isNaN(r) || r < 0 || r > 30) return toast.error("Rent rate must be 0-30");
        setSaving(true);
        try {
            const { data } = await api.put("/settings/rcm-rates", { transporter: t, rent: r });
            setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
            toast.success("RCM rates saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const backfill = async () => {
        if (!window.confirm("Retro-post RCM entries on all historical purchases that were flagged RCM but didn't create RCM ledger entries?\n\nThis is idempotent — anything already fixed is skipped.")) return;
        setBackfilling(true);
        try {
            const { data } = await api.post("/admin/backfill-rcm");
            setLastBackfill(data);
            if (data.fixed_count > 0) {
                toast.success(`Backfilled ${data.fixed_count} historical RCM entr${data.fixed_count === 1 ? "y" : "ies"}`);
            } else {
                toast.info("Nothing to backfill — all historical RCM entries are already in the ledgers");
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBackfilling(false); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Settings"
                sub="Configure the reverse-charge (RCM) GST rates the app applies when a purchase or transport line is marked RCM."
            />

            <div className="p-8 space-y-6 max-w-3xl">
                <CompanyIdentitySettings />
                <CompanyProfileCard />
                <InvoiceSeriesCard />
                <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="rcm-card">
                    <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                        <div className="font-serif text-lg">Reverse Charge (RCM) rates</div>
                        <div className="text-xs text-stone-500">
                            RCM means <b>you</b> pay the GST directly to the government (not the vendor) and simultaneously
                            claim it back as ITC. These rates drive the auto-posting on any purchase you tick RCM on.
                        </div>
                    </div>

                    <div className="p-5 space-y-6">
                        <div className="grid grid-cols-2 gap-6">
                            <RateInput
                                icon={<Truck size={16} weight="duotone" style={{ color: "#0d6efd" }} />}
                                label="Transporter (GTA)"
                                hint="Default: 5 %"
                                value={transporter}
                                onChange={setTransporter}
                                testid="rcm-transporter"
                                disabled={loading || saving}
                            />
                            <RateInput
                                icon={<House size={16} weight="duotone" style={{ color: "#20c997" }} />}
                                label="Rent (commercial)"
                                hint="Default: 18 %"
                                value={rent}
                                onChange={setRent}
                                testid="rcm-rent"
                                disabled={loading || saving}
                            />
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: "var(--se-border)" }}>
                            <div className="text-[11px] text-stone-500">
                                {meta.updated_at ? (
                                    <>Last saved on <b>{formatDate(meta.updated_at)} · {meta.updated_at.slice(11, 16)}</b></>
                                ) : "Never saved — using built-in defaults."}
                            </div>
                            <Button onClick={save} disabled={saving} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="rcm-save">
                                <FloppyDisk size={14} className="mr-2" />
                                {saving ? "Saving…" : "Save RCM rates"}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="text-xs text-stone-500 px-1">
                    Tip: when you upload a transporter invoice or a rent invoice, tick the <b>RCM</b> option on
                    the confirm screen and pick the applicable rate. The system will Cr the matching
                    <span className="mx-1 se-chip se-chip-warn">RCM Payable @ 5% / 18%</span>
                    and simultaneously Dr <span className="mx-1 se-chip se-chip-teal">RCM ITC @ 5% / 18%</span>.
                    Both appear on the Net GST page so you always know the net cash-out and ITC to claim back.
                </div>

                <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="rcm-backfill-card">
                    <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                        <div className="font-serif text-lg">Retrofit historical RCM entries</div>
                        <div className="text-xs text-stone-500">
                            Some older purchases were flagged RCM before the RCM ledger posting was live.
                            Run this once to top-up their journals so they show on the Net GST page.
                            Safe to re-run — anything already fixed is skipped.
                        </div>
                    </div>
                    <div className="p-5 flex items-center justify-between">
                        <div className="text-xs text-stone-500">
                            {lastBackfill ? (
                                <>Last run: <b>{lastBackfill.fixed_count} fixed</b> · {lastBackfill.skipped_count} skipped</>
                            ) : "Not run yet in this session."}
                        </div>
                        <Button variant="outline" onClick={backfill} disabled={backfilling} data-testid="rcm-backfill-btn">
                            {backfilling ? "Working…" : "Backfill historical RCM"}
                        </Button>
                    </div>
                    {lastBackfill?.fixed?.length ? (
                        <div className="border-t px-5 py-3 text-[11px] text-stone-600" style={{ borderColor: "var(--se-border)" }}>
                            <div className="font-medium text-stone-700 mb-1">Fixed entries:</div>
                            <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                                {lastBackfill.fixed.map((f) => (
                                    <li key={f.pid} className="font-mono">
                                        {f.vendor} · Inv {f.invoice_no} — {f.kind} ₹{f.transport_amount || f.subtotal} × {f.rcm_rate}% = ₹{f.rcm_tax}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </div>

                <PettyCashSettings />
                <WorkTimingsSettings />
                <BankAccountsSettings />
                <TransportersSettings />
                <RcmRecurringSettings />
                <PnLSettings />
                <UnitEconomicsSettings />
                <PtSettings />
                <HrMastersSettings />
                <FactoryResetSettings />
            </div>
        </>
    );
}


/* ---------------------------- Factory reset — DESTRUCTIVE ---------------------------- */
// Founder-only. Wipes every collection except the chart of accounts + users.
function FactoryResetSettings() {
    const [me, setMe] = useState(null);
    const [preview, setPreview] = useState(null);
    const [phrase, setPhrase] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => setMe(null));
    }, []);

    if (!me || me.role !== "founder") return null;

    const runDryRun = async () => {
        setLoading(true); setResult(null);
        try {
            const { data } = await api.post("/admin/factory-reset/dry-run");
            setPreview(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    const runExecute = async () => {
        if (phrase !== "RESET SIXTH ELEMENT") {
            return toast.error("Type the exact confirm phrase (case-sensitive).");
        }
        if (!window.confirm(`This will DELETE ${preview?.total_documents_to_delete || 0} documents from ${Object.keys(preview?.will_delete || {}).length} collections. This CANNOT be undone. Are you absolutely sure?`)) return;
        setLoading(true);
        try {
            const { data } = await api.post("/admin/factory-reset/execute", { confirm: phrase });
            setResult(data);
            setPreview(null);
            setPhrase("");
            toast.success(`Factory reset complete — ${data.total_documents_deleted} documents deleted.`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    return (
        <div className="bg-white border rounded-md"
             style={{ borderColor: "#fecaca" }}
             data-testid="factory-reset-card">
            <div className="px-5 py-3 border-b" style={{ borderColor: "#fecaca", background: "#fef2f2" }}>
                <div className="flex items-center gap-2">
                    <span aria-hidden="true" style={{ color: "#b91c1c" }}>⚠</span>
                    <div className="font-serif text-lg" style={{ color: "#7f1d1d" }}>Factory reset (danger zone)</div>
                </div>
                <div className="text-xs mt-1" style={{ color: "#7f1d1d" }}>
                    Wipes every journal, invoice, employee, HR record, bank statement, document, and setting. <b>Keeps only:</b> chart of accounts (ledgers, groups, petty-cash heads, purchase &amp; asset categories) and user logins. Use this for a clean production start on the same books. <b>Cannot be undone.</b>
                </div>
            </div>
            <div className="p-5 space-y-4">
                {!preview && !result ? (
                    <div>
                        <Button onClick={runDryRun} disabled={loading}
                                variant="outline"
                                style={{ color: "#b91c1c", borderColor: "#fecaca" }}
                                data-testid="factory-reset-preview-btn">
                            {loading ? "Reading…" : "Preview what will be deleted"}
                        </Button>
                        <div className="text-[11px] text-stone-500 mt-2">
                            Dry-run first shows exact counts. Nothing is deleted until you type the phrase and click Reset.
                        </div>
                    </div>
                ) : null}

                {preview ? (
                    <div className="space-y-3">
                        <div className="text-sm">
                            <div className="font-medium mb-2" style={{ color: "#7f1d1d" }}>
                                Will delete {preview.total_documents_to_delete} documents from {Object.keys(preview.will_delete).length} collections:
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-[11px] font-mono max-h-48 overflow-auto border rounded-sm p-2" style={{ borderColor: "#fecaca", background: "#fef2f2" }}>
                                {Object.entries(preview.will_delete).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                                    <div key={k}><span className="text-stone-600">{k}</span> · <b style={{ color: "#b91c1c" }}>{v}</b></div>
                                ))}
                            </div>
                        </div>
                        <div className="text-sm">
                            <div className="font-medium mb-2 text-emerald-800">Will keep intact:</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-[11px] font-mono border rounded-sm p-2" style={{ borderColor: "#a7f3d0", background: "#ecfdf5" }}>
                                {Object.entries(preview.will_keep).map(([k, v]) => (
                                    <div key={k}><span className="text-stone-600">{k}</span> · <b className="text-emerald-800">{v}</b></div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label>Type <span className="font-mono font-bold">RESET SIXTH ELEMENT</span> to confirm</Label>
                            <Input type="text" value={phrase}
                                   onChange={(e) => setPhrase(e.target.value)}
                                   placeholder="RESET SIXTH ELEMENT"
                                   className="font-mono max-w-md"
                                   disabled={loading}
                                   data-testid="factory-reset-phrase-input" />
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={runExecute}
                                    disabled={loading || phrase !== "RESET SIXTH ELEMENT"}
                                    style={{ background: "#b91c1c", color: "#fff" }}
                                    data-testid="factory-reset-execute-btn">
                                {loading ? "Deleting…" : "Reset now (cannot be undone)"}
                            </Button>
                            <Button variant="outline" onClick={() => { setPreview(null); setPhrase(""); }} disabled={loading}
                                    data-testid="factory-reset-cancel-btn">
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : null}

                {result ? (
                    <div className="border rounded-sm p-3 space-y-2"
                         style={{ borderColor: "#a7f3d0", background: "#ecfdf5" }}
                         data-testid="factory-reset-result">
                        <div className="font-medium text-emerald-800">
                            ✓ Reset complete — {result.total_documents_deleted} documents deleted
                        </div>
                        <div className="text-[11px] text-stone-600">
                            Chart of accounts + users preserved. Reload the app to see fresh state.
                        </div>
                        <Button size="sm" onClick={() => { setResult(null); }} variant="outline"
                                data-testid="factory-reset-dismiss-btn">
                            Dismiss
                        </Button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}


/* ---------------------------- Transporter RCM defaults ---------------------------- */
function TransportersSettings() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [saving, setSaving] = useState({});
    const [groupId, setGroupId] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [newName, setNewName] = useState("");
    const [newGstin, setNewGstin] = useState("");
    const [newRcm, setNewRcm] = useState(true);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        try {
            const [ledRes, grpRes] = await Promise.all([
                api.get("/ledgers", { params: { party_type: "transporter" } }),
                api.get("/account-groups"),
            ]);
            setRows(ledRes.data);
            const grp = (grpRes.data || []).find((g) =>
                g.name === "Sundry Creditors - Transporters" ||
                /transporter/i.test(g.name)
            );
            setGroupId(grp?.id || null);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const toggle = async (row) => {
        const next = !row.rcm_default;
        setSaving((s) => ({ ...s, [row.id]: true }));
        try {
            await api.patch(`/ledgers/${row.id}/transporter-rcm`, { rcm_default: next });
            setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, rcm_default: next } : r));
            toast.success(`${row.name}: RCM ${next ? "enabled" : "disabled"}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving((s) => ({ ...s, [row.id]: false })); }
    };

    const createTransporter = async () => {
        if (!newName.trim()) return toast.error("Enter a transporter name");
        if (!groupId) return toast.error("Transporter group not found — check chart of accounts");
        setCreating(true);
        try {
            const { data } = await api.post("/ledgers", {
                name: newName.trim(),
                group_id: groupId,
                party_type: "transporter",
                gstin: newGstin.trim() || null,
                opening_balance: 0,
                opening_side: "cr",
            });
            if (newRcm) {
                try {
                    await api.patch(`/ledgers/${data.id}/transporter-rcm`, { rcm_default: true });
                    data.rcm_default = true;
                } catch { /* fall through — user can toggle after */ }
            }
            setRows((rs) => [...rs, data].sort((a, b) => a.name.localeCompare(b.name)));
            toast.success(`Transporter "${newName}" added${newRcm ? " · RCM on" : ""}`);
            setNewName(""); setNewGstin(""); setNewRcm(true); setShowAdd(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCreating(false); }
    };

    const filtered = rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="transporters-card">
            <div className="px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--se-border)" }}>
                <div className="min-w-0">
                    <div className="font-serif text-lg">Transporters — RCM defaults</div>
                    <div className="text-xs text-stone-500">
                        Set <b>RCM: Yes</b> or <b>No</b> once per transporter. Every purchase/sale invoice you tag to that transporter
                        auto-applies (or skips) the RCM entry — no per-invoice question, no manual toggle.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Input placeholder="Search transporter…" value={q} onChange={(e) => setQ(e.target.value)}
                           className="w-56 h-8 text-xs" data-testid="transporter-search" />
                    <Button size="sm"
                            onClick={() => setShowAdd((v) => !v)}
                            style={{ background: showAdd ? "#9ca3af" : "var(--se-brand)", color: "#fff" }}
                            data-testid="transporter-add-btn">
                        {showAdd ? "Cancel" : "+ Add transporter"}
                    </Button>
                </div>
            </div>

            {showAdd ? (
                <div className="px-5 py-4 border-b flex items-end gap-3 flex-wrap"
                     style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}
                     data-testid="transporter-add-form">
                    <div className="flex-1 min-w-[200px]">
                        <Label className="text-[10px] uppercase tracking-widest">Name *</Label>
                        <Input value={newName}
                               onChange={(e) => setNewName(e.target.value)}
                               placeholder="e.g. VRL Logistics"
                               className="h-9 text-sm" data-testid="transporter-new-name" />
                    </div>
                    <div className="w-48">
                        <Label className="text-[10px] uppercase tracking-widest">GSTIN (optional)</Label>
                        <Input value={newGstin}
                               onChange={(e) => setNewGstin(e.target.value.toUpperCase())}
                               placeholder="15-char GSTIN"
                               maxLength={15}
                               className="h-9 text-xs font-mono" data-testid="transporter-new-gstin" />
                    </div>
                    <label className="flex items-center gap-2 h-9 cursor-pointer">
                        <input type="checkbox" checked={newRcm}
                               onChange={(e) => setNewRcm(e.target.checked)}
                               data-testid="transporter-new-rcm" />
                        <span className="text-xs">Apply RCM by default</span>
                    </label>
                    <Button onClick={createTransporter} disabled={creating || !newName.trim()}
                            style={{ background: "var(--se-brand)", color: "#fff" }}
                            data-testid="transporter-new-save">
                        {creating ? "Saving…" : "Save"}
                    </Button>
                </div>
            ) : null}

            <div className="p-5">
                {filtered.length === 0 ? (
                    <div className="text-xs text-stone-500 text-center py-6">
                        {rows.length === 0
                            ? "No transporter ledgers yet. Click Add transporter above, or create one from any purchase invoice&apos;s freight section."
                            : "No transporters match your search."}
                    </div>
                ) : (
                    <div className="border rounded-md divide-y" style={{ borderColor: "var(--se-border)" }}>
                        {filtered.map((r) => (
                            <div key={r.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`transporter-row-${r.id}`}>
                                <div>
                                    <div className="font-medium">{r.name}</div>
                                    {r.gstin ? <div className="text-[10px] text-stone-500 font-mono">{r.gstin}</div> : null}
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] uppercase tracking-wider text-stone-500 mr-1">RCM</span>
                                    <div className="inline-flex rounded-md border overflow-hidden"
                                         style={{ borderColor: "var(--se-border)" }}>
                                        {[
                                            { v: true,  label: "Yes" },
                                            { v: false, label: "No" },
                                        ].map((opt) => {
                                            const active = !!r.rcm_default === opt.v;
                                            return (
                                                <button key={String(opt.v)} type="button"
                                                        disabled={!!saving[r.id]}
                                                        onClick={() => (active ? null : toggle(r))}
                                                        className="px-3 py-1 text-xs font-medium disabled:opacity-50"
                                                        style={{
                                                            background: active
                                                                ? (opt.v ? "var(--se-brand)" : "#6b7280")
                                                                : "#fff",
                                                            color: active ? "#fff" : "#6b7280",
                                                        }}
                                                        data-testid={`transporter-rcm-${opt.v ? "yes" : "no"}-${r.id}`}>
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ---------------- Recurring expenses with RCM applied ---------------- */
/* Companion panel to Transporters — surfaces every recurring expense flagged as
 * RCM applicable (e.g. rent from unregistered landlord) so the founder sees all
 * RCM configuration in one place next to the Transporters list. Read-only here;
 * changes happen on the Recurring Expenses page.
 */
function RcmRecurringSettings() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/recurring-expenses");
                setRows((data || []).filter((r) => r.rcm_applicable && !r.archived_at));
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="rcm-recurring-card">
            <div className="px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: "var(--se-border)" }}>
                <div className="min-w-0">
                    <div className="font-serif text-lg">Rent & other RCM recurrings</div>
                    <div className="text-xs text-stone-500">
                        Recurring expenses (non-GST rents etc.) where <b>RCM auto-applies every month</b>.
                        Toggle happens on the Recurring Expenses page — this list is read-only.
                    </div>
                </div>
                <a href="/recurring-expenses" className="text-xs px-3 py-1.5 rounded-sm border hover:bg-stone-50" style={{ borderColor: "var(--se-border)", color: "var(--se-brand)" }} data-testid="rcm-recurring-manage">
                    Manage →
                </a>
            </div>
            <div className="p-5">
                {loading ? (
                    <div className="text-sm text-stone-500">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="text-sm text-stone-500" data-testid="rcm-recurring-empty">
                        No recurring expenses have RCM on. When you add a rent recurring for an unregistered landlord, flip the <b>Does RCM apply?</b> toggle and it&apos;ll appear here.
                    </div>
                ) : (
                    <table className="se-table" data-testid="rcm-recurring-list">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Category</th>
                                <th className="text-right">Monthly ₹</th>
                                <th className="text-center">RCM rate</th>
                                <th>Vendor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const rate = (r.category || "").toLowerCase().includes("rent") ? "18%" : "5%";
                                return (
                                    <tr key={r.id} data-testid={`rcm-recurring-row-${r.id}`}>
                                        <td className="font-medium">{r.name}</td>
                                        <td className="text-xs text-stone-500 capitalize">{r.category || "—"}</td>
                                        <td className="se-num">₹ {inrFmt(Number(r.amount || 0))}</td>
                                        <td className="text-center"><span className="se-chip se-chip-teal">{rate}</span></td>
                                        <td className="text-xs text-stone-500">{r.vendor_name || "—"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

/* ---------------------------- Professional Tax (PT) ---------------------------- */
function PtSettings() {
    const [threshold, setThreshold] = useState("");
    const [amount, setAmount] = useState("");
    const [meta, setMeta] = useState({ updated_at: null });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/settings/pt");
            setThreshold(String(data.threshold_monthly ?? ""));
            setAmount(String(data.deduction_monthly ?? ""));
            setMeta({ updated_at: data.updated_at });
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        const t = Number(threshold);
        const a = Number(amount);
        if (Number.isNaN(t) || t < 0) return toast.error("Threshold must be ≥ 0");
        if (Number.isNaN(a) || a < 0) return toast.error("Deduction amount must be ≥ 0");
        setSaving(true);
        try {
            const { data } = await api.put("/settings/pt", { threshold_monthly: t, deduction_monthly: a });
            setMeta({ updated_at: data.updated_at });
            toast.success("Professional Tax settings saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="pt-card">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg">Professional Tax (PT)</div>
                <div className="text-xs text-stone-500">
                    Karnataka slab is typically <b>₹200/month above ₹15,000</b> or <b>₹300/month above ₹25,000</b>.
                    Set your threshold and monthly deduction once — from then on, every payroll run compares each
                    employee&apos;s <b>monthly gross earned</b> against the threshold and, if the employee&apos;s card has
                    <b> Deduct PT</b> ticked, deducts the amount and credits <b>PT Payable</b>.
                </div>
            </div>
            <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <Label>Salary threshold (₹/month)</Label>
                        <Input type="number" min="0" value={threshold}
                               onChange={(e) => setThreshold(e.target.value)}
                               data-testid="pt-threshold" />
                        <div className="text-[10px] text-stone-500 mt-1">
                            PT kicks in only when the person&apos;s <b>gross_earned</b> for that month is ≥ this value.
                        </div>
                    </div>
                    <div>
                        <Label>PT amount (₹/month)</Label>
                        <Input type="number" min="0" value={amount}
                               onChange={(e) => setAmount(e.target.value)}
                               data-testid="pt-amount" />
                        <div className="text-[10px] text-stone-500 mt-1">
                            Set to <b>0</b> to disable PT globally, even for opted-in employees.
                        </div>
                    </div>
                </div>
                <div className="text-[11px] text-stone-600 p-3 rounded-md" style={{ background: "#eef2ff" }}>
                    Example: employee earns ₹30,000 gross this month, has PF ₹1,800 &amp; TDS ₹0, and their card has PT ticked.
                    → Net to employee <b>₹27,900</b>, and <b>₹300</b> sits in PT Payable until you settle with the state.
                </div>
                <div className="flex items-center justify-between">
                    <div className="text-[10px] text-stone-500">
                        {meta.updated_at ? `Last updated ${formatDate(meta.updated_at)}` : "Using defaults."}
                    </div>
                    <Button onClick={save} disabled={saving} style={{ background: "var(--se-brand)", color: "#fff" }}
                            data-testid="pt-save">
                        {saving ? "Saving…" : "Save Professional Tax"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

/* ---------------------------- HR Masters (Departments + Locations) ---------------------------- */
function HrMastersSettings() {
    const [departments, setDepartments] = useState([]);
    const [locations, setLocations] = useState([]);
    const [meta, setMeta] = useState({ updated_at: null });
    const [newDept, setNewDept] = useState("");
    const [newLoc, setNewLoc] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/settings/hr-masters");
            setDepartments(data.departments || []);
            setLocations(data.locations || []);
            setMeta({ updated_at: data.updated_at });
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const save = async (next) => {
        setSaving(true);
        try {
            const { data } = await api.put("/settings/hr-masters", next);
            setDepartments(data.departments || []);
            setLocations(data.locations || []);
            setMeta({ updated_at: data.updated_at });
            toast.success("Saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const addDept = () => {
        const v = newDept.trim();
        if (!v) return;
        if (departments.some((d) => d.toLowerCase() === v.toLowerCase())) {
            toast.info("Already exists");
            return;
        }
        setNewDept("");
        save({ departments: [...departments, v], locations });
    };
    const removeDept = (d) => {
        if (departments.length <= 1) { toast.error("At least one department is required"); return; }
        if (!window.confirm(`Remove "${d}"? Existing employees using it will keep their value; new employees just won't see it in the dropdown.`)) return;
        save({ departments: departments.filter((x) => x !== d), locations });
    };
    const addLoc = () => {
        const v = newLoc.trim();
        if (!v) return;
        if (locations.some((l) => l.toLowerCase() === v.toLowerCase())) {
            toast.info("Already exists");
            return;
        }
        setNewLoc("");
        save({ departments, locations: [...locations, v] });
    };
    const removeLoc = (l) => {
        if (locations.length <= 1) { toast.error("At least one location is required"); return; }
        if (!window.confirm(`Remove "${l}"? Existing employees using it will keep their value; new employees just won't see it in the dropdown.`)) return;
        save({ departments, locations: locations.filter((x) => x !== l) });
    };

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="hr-masters-card">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg">Department &amp; Location dropdowns</div>
                <div className="text-xs text-stone-500">
                    Controls the picker options in the New Employee form. Keep this list tight to avoid typos (&quot;Prod&quot; vs &quot;Production&quot;) polluting your drill-downs.
                </div>
            </div>
            <div className="p-5 grid grid-cols-2 gap-6">
                <div>
                    <div className="text-sm font-medium mb-2">Departments</div>
                    <div className="space-y-1.5 mb-3">
                        {departments.map((d) => (
                            <div key={d} className="flex items-center justify-between px-3 py-1.5 border rounded-sm text-sm"
                                 style={{ borderColor: "var(--se-border)", background: "#fafafa" }}
                                 data-testid={`hr-dept-${d}`}>
                                <span>{d}</span>
                                <button onClick={() => removeDept(d)} disabled={saving}
                                        className="text-xs text-rose-600 hover:underline"
                                        data-testid={`hr-dept-remove-${d}`}>Remove</button>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Input placeholder="Add department…" value={newDept}
                               onChange={(e) => setNewDept(e.target.value)}
                               onKeyDown={(e) => e.key === "Enter" && addDept()}
                               data-testid="hr-dept-input" />
                        <Button onClick={addDept} disabled={saving || !newDept.trim()}
                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                data-testid="hr-dept-add">Add</Button>
                    </div>
                </div>
                <div>
                    <div className="text-sm font-medium mb-2">Locations</div>
                    <div className="space-y-1.5 mb-3">
                        {locations.map((l) => (
                            <div key={l} className="flex items-center justify-between px-3 py-1.5 border rounded-sm text-sm"
                                 style={{ borderColor: "var(--se-border)", background: "#fafafa" }}
                                 data-testid={`hr-loc-${l}`}>
                                <span>{l}</span>
                                <button onClick={() => removeLoc(l)} disabled={saving}
                                        className="text-xs text-rose-600 hover:underline"
                                        data-testid={`hr-loc-remove-${l}`}>Remove</button>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Input placeholder="Add location…" value={newLoc}
                               onChange={(e) => setNewLoc(e.target.value)}
                               onKeyDown={(e) => e.key === "Enter" && addLoc()}
                               data-testid="hr-loc-input" />
                        <Button onClick={addLoc} disabled={saving || !newLoc.trim()}
                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                data-testid="hr-loc-add">Add</Button>
                    </div>
                </div>
                <div className="col-span-2 text-[10px] text-stone-500">
                    {meta.updated_at ? `Last updated ${formatDate(meta.updated_at)}` : "Using defaults."}
                </div>
            </div>
        </div>
    );
}

function RateInput({ icon, label, hint, value, onChange, testid, disabled }) {
    return (
        <div>
            <Label className="flex items-center gap-2 text-sm">
                {icon} {label}
            </Label>
            <div className="mt-1 relative">
                <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="30"
                    value={value}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                    className="pr-8"
                    data-testid={testid}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">%</span>
            </div>
            <div className="text-[11px] text-stone-500 mt-1">{hint}</div>
        </div>
    );
}


function CompanyIdentitySettings() {
    // Global company identity. Used across GST detection (intra vs inter-state
    // split), buyer-name auto-verification on uploaded bills, and every
    // Input GST / RCM computation. Not petty-cash-specific — it lives at the
    // top of Settings so the founder can find it in one click.
    const [companyName, setCompanyName] = useState("");
    const [companyGstin, setCompanyGstin] = useState("");
    const [manualState, setManualState] = useState("27");
    const [derivedFromGstin, setDerivedFromGstin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [hs, cn] = await Promise.all([
                api.get("/petty-cash/settings/home-state"),
                api.get("/petty-cash/settings/company-name"),
            ]);
            setCompanyGstin(hs.data.company_gstin || "");
            setDerivedFromGstin(!!hs.data.derived_from_gstin);
            setManualState(String(hs.data.value ?? "27"));
            setCompanyName(cn.data.value || "");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const jobs = [
                api.put("/petty-cash/settings/company-gstin", { value: companyGstin.trim().toUpperCase() }),
                api.put("/petty-cash/settings/company-name", { value: companyName.trim() }),
            ];
            // Manual state code is only accepted when there's no GSTIN
            if (!companyGstin.trim() && /^\d{2}$/.test(manualState)) {
                jobs.push(api.put("/petty-cash/settings/home-state", { value: manualState }));
            }
            await Promise.all(jobs);
            toast.success("Company identity saved");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const derivedState = companyGstin && companyGstin.length >= 2 ? companyGstin.slice(0, 2) : null;

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}
             data-testid="company-identity-card">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="flex items-center gap-2">
                    <span aria-hidden="true" style={{ color: "#7c3aed" }}>🏛</span>
                    <div className="font-serif text-lg">Company identity</div>
                </div>
                <div className="text-xs text-stone-500">
                    Your legal name, GSTIN, and home state. Used everywhere — every purchase / sales / expense-claim / petty-cash bill checks against these to decide intra-vs-inter GST, ITC eligibility, and whether the bill is actually addressed to you.
                </div>
            </div>
            <div className="p-5 space-y-4">
                <div>
                    <Label>Legal company name</Label>
                    <Input type="text" value={companyName}
                           onChange={(e) => setCompanyName(e.target.value)}
                           placeholder="Sixth Element Furnishing Solutions Pvt Ltd"
                           maxLength={120} disabled={loading}
                           data-testid="company-name-input" />
                    <div className="text-[11px] text-stone-500 mt-1">
                        The AI reads the buyer name printed on every uploaded GST bill and loosely matches against this — variants like <i>Pvt Ltd</i>, <i>Private Limited</i>, <i>M/s</i> are handled. If a bill is addressed to someone else, ITC is refused.
                    </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                    <div>
                        <Label>Company GSTIN</Label>
                        <Input type="text" value={companyGstin}
                               onChange={(e) => setCompanyGstin(e.target.value.toUpperCase().slice(0, 15))}
                               placeholder="29AAVCS8119A1ZZ" maxLength={15}
                               disabled={loading} data-testid="company-gstin-input" />
                        <div className="text-[11px] text-stone-500 mt-1">
                            {companyGstin && companyGstin.length === 15 ? (
                                <>Home state <b>auto-derived</b> → <b>{derivedState}</b> (first two digits of GSTIN). Change GSTIN → home state auto-updates.</>
                            ) : (
                                <>Once set, home state is derived from digits 1&ndash;2. No more manual state code guessing.</>
                            )}
                        </div>
                    </div>
                    <div>
                        <Label>Home state code {derivedFromGstin ? <span className="text-[10px] text-stone-500">(read-only — derived from GSTIN)</span> : null}</Label>
                        <Input type="text"
                               value={derivedFromGstin ? (derivedState || manualState) : manualState}
                               onChange={(e) => setManualState(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
                               placeholder="27"
                               disabled={loading || derivedFromGstin}
                               className="max-w-[8rem]"
                               data-testid="home-state-input" />
                        <div className="text-[11px] text-stone-500 mt-1">
                            {derivedFromGstin
                                ? "Clear the GSTIN above to type a manual state code here."
                                : "2-digit Indian state code. 27 = Maharashtra, 29 = Karnataka, 07 = Delhi, 33 = Tamil Nadu, etc."}
                        </div>
                    </div>
                </div>

                <div>
                    <Button onClick={save} disabled={loading || saving}
                            style={{ background: "#7c3aed", color: "#fff" }}
                            data-testid="company-identity-save">
                        <FloppyDisk size={14} className="mr-2" />
                        {saving ? "Saving…" : "Save company identity"}
                    </Button>
                </div>
            </div>
        </div>
    );
}


function PettyCashSettings() {
    const [cap, setCap] = useState("2000");
    const [heads, setHeads] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingCap, setSavingCap] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [s, lg] = await Promise.all([
                api.get("/petty-cash/settings"),
                api.get("/ledgers"),
            ]);
            setCap(String(s.data.cap ?? "2000"));
            setHeads(s.data.heads || []);
            setLedgers(lg.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const saveCap = async () => {
        const v = Number(cap);
        if (Number.isNaN(v) || v < 0) return toast.error("Cap must be a positive number");
        setSavingCap(true);
        try {
            await api.put("/petty-cash/settings/cap", { value: v });
            toast.success("Saved");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingCap(false); }
    };

    const deleteHead = async (h) => {
        if (!window.confirm(`Delete "${h.name}"? Historical expenses using it will still show the name.`)) return;
        try { await api.delete(`/petty-cash/settings/heads/${h.id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="pc-settings-card">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="flex items-center gap-2">
                    <Wallet size={16} weight="duotone" style={{ color: "#f59e0b" }} />
                    <div className="font-serif text-lg">Petty cash</div>
                </div>
                <div className="text-xs text-stone-500">
                    Set the request cap and the expense heads employees can choose from.
                    Each head is mapped to a real ledger so the accounting stays clean.
                </div>
            </div>

            <div className="p-5 space-y-6">
                <div>
                    <Label>Unaccounted holding limit (₹)</Label>
                    <Input type="number" value={cap} onChange={(e) => setCap(e.target.value)}
                           disabled={loading || savingCap} className="max-w-xs" data-testid="pc-cap-input" />
                    <div className="text-[11px] text-stone-500 mt-1">
                        An employee cannot raise a new request while they&apos;re already holding this much (or more) unaccounted. Request amount itself is uncapped.
                    </div>
                </div>
                <div>
                    <Button onClick={saveCap} disabled={savingCap} style={{ background: "#f59e0b", color: "#fff" }} data-testid="pc-cap-save">
                        <FloppyDisk size={14} className="mr-2" /> {savingCap ? "Saving…" : "Save cap"}
                    </Button>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium">Expense heads ({heads.length})</div>
                        <Button variant="outline" onClick={() => { setEditing(null); setDialogOpen(true); }}
                                disabled={ledgers.length === 0} data-testid="pc-head-add-btn">+ Add head</Button>
                    </div>
                    <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                        <table className="se-table text-sm" data-testid="pc-heads-table">
                            <thead><tr><th>Name</th><th>Ledger</th><th>Dept</th><th>Location</th><th>D / I</th><th></th></tr></thead>
                            <tbody>
                                {heads.length === 0
                                    ? <tr><td colSpan="6" className="py-6 text-center text-stone-500">No heads yet. Add ones like Fuel, Food, Auto, Stationery — each mapped to a real ledger.</td></tr>
                                    : heads.map((h) => (
                                        <tr key={h.id} data-testid={`pc-head-row-${h.id}`}>
                                            <td className="font-medium">{h.name}</td>
                                            <td className="text-xs text-stone-500">{h.ledger_name}</td>
                                            <td className="text-xs text-stone-500">{h.default_dept || "—"}</td>
                                            <td className="text-xs text-stone-500">{h.default_location || "—"}</td>
                                            <td className="text-xs text-stone-500">{h.default_direct_indirect || "—"}</td>
                                            <td>
                                                <div className="flex gap-2 justify-end">
                                                    <Button size="sm" variant="ghost" onClick={() => { setEditing(h); setDialogOpen(true); }}
                                                            data-testid={`pc-head-edit-${h.id}`}>Edit</Button>
                                                    <Button size="sm" variant="ghost" onClick={() => deleteHead(h)}
                                                            className="text-rose-600" data-testid={`pc-head-delete-${h.id}`}>Delete</Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <HeadDialog open={dialogOpen} onOpenChange={setDialogOpen} onDone={load} ledgers={ledgers} editing={editing} />
        </div>
    );
}

function HeadDialog({ open, onOpenChange, onDone, ledgers, editing }) {
    const [name, setName] = useState("");
    const [ledgerId, setLedgerId] = useState("");
    const [dept, setDept] = useState("");
    const [location, setLocation] = useState("");
    const [di, setDi] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(editing?.name || "");
            setLedgerId(editing?.ledger_id || "");
            setDept(editing?.default_dept || "");
            setLocation(editing?.default_location || "");
            setDi(editing?.default_direct_indirect || "");
        }
    }, [open, editing]);

    const submit = async () => {
        if (!name.trim()) return toast.error("Name required");
        if (!ledgerId) return toast.error("Pick a ledger");
        setSaving(true);
        try {
            const body = { name: name.trim(), ledger_id: ledgerId, default_dept: dept || null,
                           default_location: location || null, default_direct_indirect: di || null };
            if (editing) {
                await api.patch(`/petty-cash/settings/heads/${editing.id}`, body);
                toast.success("Updated");
            } else {
                await api.post("/petty-cash/settings/heads", body);
                toast.success("Added");
            }
            onOpenChange(false);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    // Filter to expense-like ledgers (all except party/bank/cash which shouldn't be expense heads)
    const eligible = ledgers.filter((l) => !["bank", "cash", "customer", "vendor", "transporter"].includes(l.party_type));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="pc-head-dialog">
                <DialogHeader><DialogTitle>{editing ? "Edit expense head" : "Add expense head"}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div><Label>Name *</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Fuel, Food, Auto, Stationery…" data-testid="pc-head-name" /></div>
                    <div>
                        <Label>Ledger to post to *</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm" value={ledgerId}
                                onChange={(e) => setLedgerId(e.target.value)} data-testid="pc-head-ledger">
                            <option value="">— Select ledger —</option>
                            {eligible.map((l) => (
                                <option key={l.id} value={l.id}>{l.name} ({l.group_name})</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Default dept</Label>
                            <Input value={dept} onChange={(e) => setDept(e.target.value)} placeholder="Ops" data-testid="pc-head-dept" /></div>
                        <div><Label>Default location</Label>
                            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Main Factory" data-testid="pc-head-location" /></div>
                    </div>
                    <div>
                        <Label>Direct / Indirect</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm" value={di}
                                onChange={(e) => setDi(e.target.value)} data-testid="pc-head-di">
                            <option value="">Not tagged</option>
                            <option value="direct">Direct</option>
                            <option value="indirect">Indirect</option>
                        </select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="pc-head-submit">
                        {saving ? "Saving…" : (editing ? "Save" : "Add")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Bank Accounts — add a new bank with an opening balance, close a bank
// (optionally discarding a leftover balance to Miscellaneous Expenses).
// ---------------------------------------------------------------------------
function inrFmt(n) {
    if (n == null || Number.isNaN(Number(n))) return "0.00";
    return Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function BankAccountsSettings() {
    const [banks, setBanks] = useState([]);
    const [balances, setBalances] = useState({}); // id -> balance
    const [loading, setLoading] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [addForm, setAddForm] = useState({ name: "", opening_balance: "", opening_date: new Date().toISOString().slice(0, 10) });
    const [creating, setCreating] = useState(false);
    const [closeTarget, setCloseTarget] = useState(null);
    const [closeReason, setCloseReason] = useState("");
    const [closing, setClosing] = useState(false);
    const [openingTarget, setOpeningTarget] = useState(null);
    const [openingForm, setOpeningForm] = useState({ opening_balance: "", opening_date: "" });
    const [savingOpening, setSavingOpening] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [banksRes, cbpRes] = await Promise.all([
                api.get("/banks"),
                api.get("/reports/cash-bank-position"),
            ]);
            setBanks(banksRes.data || []);
            const bal = {};
            for (const acc of (cbpRes.data?.accounts || [])) bal[acc.id] = acc.balance;
            setBalances(bal);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        const nm = addForm.name.trim();
        if (!nm) return toast.error("Bank name is required");
        const amt = addForm.opening_balance === "" ? 0 : Number(addForm.opening_balance);
        if (Number.isNaN(amt)) return toast.error("Opening balance must be a number");
        setCreating(true);
        try {
            await api.post("/banks", {
                name: nm,
                opening_balance: amt,
                opening_date: addForm.opening_date || null,
            });
            toast.success(`Bank '${nm}' added`);
            setShowAdd(false);
            setAddForm({ name: "", opening_balance: "", opening_date: new Date().toISOString().slice(0, 10) });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCreating(false); }
    };

    const close = async () => {
        if (!closeTarget) return;
        const bal = balances[closeTarget.id] || 0;
        const mode = bal !== 0 ? "discard" : null;
        setClosing(true);
        try {
            const { data } = await api.post(`/banks/${closeTarget.id}/close`, {
                reason: closeReason || null,
                mode,
            });
            if (data.balance_discarded) {
                toast.success(`Closed '${closeTarget.name}'. ₹${inrFmt(Math.abs(data.balance_discarded))} written off to Miscellaneous Expenses.`);
            } else {
                toast.success(`Closed '${closeTarget.name}'.`);
            }
            setCloseTarget(null);
            setCloseReason("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setClosing(false); }
    };

    const targetBal = closeTarget ? (balances[closeTarget.id] || 0) : 0;

    const openEditOpening = (b) => {
        setOpeningTarget(b);
        // Show a signed amount (negative = overdraft) so the founder edits a
        // single value instead of juggling a Dr/Cr flag.
        const raw = Number(b.opening_balance || 0);
        const signed = b.opening_side === "cr" ? -raw : raw;
        setOpeningForm({
            opening_balance: signed === 0 ? "" : String(signed),
            opening_date: b.opening_date || new Date().toISOString().slice(0, 10),
        });
    };

    const saveOpening = async () => {
        if (!openingTarget) return;
        const amt = openingForm.opening_balance === "" ? 0 : Number(openingForm.opening_balance);
        if (Number.isNaN(amt)) return toast.error("Opening balance must be a number");
        if (!openingForm.opening_date) return toast.error("Opening date is required");
        setSavingOpening(true);
        try {
            await api.patch(`/ledgers/${openingTarget.id}/opening`, {
                opening_balance: Math.abs(amt),
                opening_side: amt >= 0 ? "dr" : "cr",
                opening_date: openingForm.opening_date,
            });
            toast.success(`Opening balance saved for ${openingTarget.name}`);
            setOpeningTarget(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingOpening(false); }
    };

    const banksWithoutOpening = banks.filter((b) => !b.opening_date && !/fiat/i.test(b.name));

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="bank-accounts-card">
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                <div>
                    <div className="font-serif text-lg">Bank accounts</div>
                    <div className="text-xs text-stone-500">
                        Add or close bank ledgers. Closing a bank with a non-zero balance writes it off to Miscellaneous Expenses so the books stay balanced.
                    </div>
                </div>
                <Button onClick={() => setShowAdd(true)} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="bank-add-btn">
                    <Wallet size={14} className="mr-2" /> Add bank
                </Button>
            </div>
            <div className="p-5 space-y-3">
                {banksWithoutOpening.length > 0 ? (
                    <div className="border rounded-sm p-3 text-sm flex items-start justify-between gap-3"
                         style={{ background: "#fef2f2", color: "#7f1d1d", borderColor: "#fecaca" }}
                         data-testid="banks-missing-opening-warning">
                        <div>
                            <div className="font-semibold">Opening balance not set for {banksWithoutOpening.length} bank{banksWithoutOpening.length === 1 ? "" : "s"}.</div>
                            <div className="text-xs mt-1">
                                Statement uploads will be blocked for banks that don&apos;t have an opening balance and start date. Click <b>Set opening</b> on {banksWithoutOpening.map((b) => b.name).join(", ")}.
                            </div>
                        </div>
                    </div>
                ) : null}
                {loading ? (
                    <div className="text-sm text-stone-500">Loading…</div>
                ) : banks.length === 0 ? (
                    <div className="text-sm text-stone-500">No bank ledgers yet.</div>
                ) : (
                    <table className="se-table" data-testid="bank-list">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Group</th>
                                <th className="text-right">Opening ₹</th>
                                <th>Opening date</th>
                                <th className="text-right">Balance</th>
                                <th>Type</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {banks.map((b) => {
                                const bal = balances[b.id] ?? 0;
                                const isSystem = !!b.system;
                                const isFiat = /fiat/i.test(b.name);
                                const openingRaw = Number(b.opening_balance || 0);
                                const openingSigned = b.opening_side === "cr" ? -openingRaw : openingRaw;
                                const openingMissing = !b.opening_date && !isFiat;
                                return (
                                    <tr key={b.id} data-testid={`bank-row-${b.id}`}>
                                        <td className="font-medium">{b.name}</td>
                                        <td className="text-xs text-stone-500">{b.group_name}</td>
                                        <td className="se-num" data-testid={`bank-opening-amt-${b.id}`}
                                            style={{ color: openingSigned < 0 ? "#991b1b" : "#111" }}>
                                            {openingSigned === 0 && !b.opening_date ? "—" : `${openingSigned < 0 ? "− " : ""}₹ ${inrFmt(Math.abs(openingSigned))}`}
                                        </td>
                                        <td className="font-mono text-xs" data-testid={`bank-opening-date-${b.id}`}>
                                            {b.opening_date ? b.opening_date : (
                                                <span className="text-rose-700 font-sans">not set</span>
                                            )}
                                        </td>
                                        <td className="se-num" style={{ color: bal < 0 ? "#991b1b" : "#111" }}>
                                            {bal < 0 ? "− " : ""}₹ {inrFmt(Math.abs(bal))}
                                        </td>
                                        <td>
                                            <span className={`se-chip ${isSystem ? "se-chip-teal" : "se-chip-posted"}`}>
                                                {isSystem ? "system" : "custom"}
                                            </span>
                                        </td>
                                        <td className="text-right">
                                            <div className="flex gap-1 justify-end">
                                                {isFiat ? (
                                                    <span className="text-[11px] text-stone-400" title="Cash pool — no bank statement">cash</span>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        variant={openingMissing ? "default" : "outline"}
                                                        onClick={() => openEditOpening(b)}
                                                        data-testid={`bank-opening-edit-${b.id}`}
                                                        style={openingMissing ? { background: "var(--se-brand)", color: "#fff" } : undefined}
                                                    >
                                                        {openingMissing ? "Set opening" : "Edit opening"}
                                                    </Button>
                                                )}
                                                {isSystem ? null : (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => { setCloseTarget(b); setCloseReason(""); }}
                                                        data-testid={`bank-close-${b.id}`}
                                                    >
                                                        Close
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Edit-opening dialog */}
            <Dialog open={!!openingTarget} onOpenChange={(v) => !v && setOpeningTarget(null)}>
                <DialogContent data-testid="bank-opening-dialog">
                    <DialogHeader>
                        <DialogTitle>Opening balance — {openingTarget?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="text-xs text-stone-600">
                            Enter the balance you can see in your bank on the software&apos;s start date.
                            Statement uploads will chain from this anchor — every row&apos;s closing balance
                            has to add up, otherwise the upload will be rejected as incomplete.
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Opening balance (₹)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={openingForm.opening_balance}
                                    onChange={(e) => setOpeningForm({ ...openingForm, opening_balance: e.target.value })}
                                    placeholder="e.g. 100000.00"
                                    data-testid="bank-opening-amt-input"
                                />
                                <div className="text-[10px] text-stone-500 mt-1">Positive = money in the bank. Negative = overdraft.</div>
                            </div>
                            <div>
                                <Label>As of date *</Label>
                                <Input
                                    type="date"
                                    value={openingForm.opening_date}
                                    onChange={(e) => setOpeningForm({ ...openingForm, opening_date: e.target.value })}
                                    data-testid="bank-opening-date-input"
                                />
                                <div className="text-[10px] text-stone-500 mt-1">e.g. 2026-04-01</div>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpeningTarget(null)} disabled={savingOpening}>Cancel</Button>
                        <Button onClick={saveOpening} disabled={savingOpening}
                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                data-testid="bank-opening-save">
                            {savingOpening ? "Saving…" : "Save opening"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add-bank dialog */}
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent data-testid="bank-add-dialog">
                    <DialogHeader><DialogTitle>Add a new bank account</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Bank name *</Label>
                            <Input
                                value={addForm.name}
                                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                                placeholder="e.g. HDFC Current A/c 0123"
                                data-testid="bank-add-name"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Opening balance (₹)</Label>
                                <Input
                                    type="number"
                                    value={addForm.opening_balance}
                                    onChange={(e) => setAddForm({ ...addForm, opening_balance: e.target.value })}
                                    placeholder="0.00"
                                    data-testid="bank-add-opening"
                                />
                                <div className="text-[10px] text-stone-500 mt-1">Positive = money in the bank. Negative = overdraft.</div>
                            </div>
                            <div>
                                <Label>Opening date</Label>
                                <Input
                                    type="date"
                                    value={addForm.opening_date}
                                    onChange={(e) => setAddForm({ ...addForm, opening_date: e.target.value })}
                                    data-testid="bank-add-opening-date"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAdd(false)} disabled={creating}>Cancel</Button>
                        <Button onClick={create} disabled={creating} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="bank-add-submit">
                            {creating ? "Adding…" : "Add bank"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Close-bank dialog */}
            <Dialog open={!!closeTarget} onOpenChange={(v) => !v && setCloseTarget(null)}>
                <DialogContent data-testid="bank-close-dialog">
                    <DialogHeader><DialogTitle>Close {closeTarget?.name || "bank"}</DialogTitle></DialogHeader>
                    <div className="space-y-3 text-sm">
                        {targetBal === 0 ? (
                            <div className="text-stone-600">
                                Current balance is ₹ 0.00 — the bank will be marked inactive and hidden from all pickers. Historical journal entries stay intact.
                            </div>
                        ) : (
                            <div className="rounded-sm border p-3" style={{ borderColor: "#fecaca", background: "#fef2f2" }}>
                                <div className="text-xs uppercase tracking-widest text-rose-700">Balance still open</div>
                                <div className="text-lg font-mono mt-1" style={{ color: targetBal < 0 ? "#991b1b" : "#111" }}>
                                    {targetBal < 0 ? "− " : ""}₹ {inrFmt(Math.abs(targetBal))}
                                </div>
                                <div className="text-xs text-stone-600 mt-2">
                                    Closing now will <b>write this off to Miscellaneous Expenses</b> so the books stay balanced.
                                    Historical entries stay intact — only the leftover balance is discarded.
                                </div>
                            </div>
                        )}
                        <div>
                            <Label>Reason (optional)</Label>
                            <Input
                                value={closeReason}
                                onChange={(e) => setCloseReason(e.target.value)}
                                placeholder="e.g. Account closed at branch on 2026-02-15"
                                data-testid="bank-close-reason"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCloseTarget(null)} disabled={closing}>Cancel</Button>
                        <Button
                            onClick={close}
                            disabled={closing}
                            style={{ background: targetBal === 0 ? "#57534e" : "#e11d48", color: "#fff" }}
                            data-testid="bank-close-confirm"
                        >
                            {closing
                                ? "Closing…"
                                : targetBal === 0
                                    ? "Close bank"
                                    : "Close & discard balance"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}



function WorkTimingsSettings() {
    const [wt, setWt] = useState({
        start_time: "09:00", end_time: "21:00",
        lunch_break_mins: 60, tea_break_count: 2, tea_break_mins_each: 30,
        weekly_holidays: [6],
    });
    const [hoursPerDay, setHoursPerDay] = useState(10);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/hr/work-timings");
            const { hours_per_day, ...rest } = data;
            setWt(rest);
            setHoursPerDay(hours_per_day);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                ...wt,
                lunch_break_mins: Number(wt.lunch_break_mins) || 0,
                tea_break_count: Number(wt.tea_break_count) || 0,
                tea_break_mins_each: Number(wt.tea_break_mins_each) || 0,
                weekly_holidays: wt.weekly_holidays.map(Number),
            };
            const { data } = await api.put("/hr/work-timings", body);
            setHoursPerDay(data.hours_per_day);
            toast.success("Saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const showThisMonth = async () => {
        try {
            const month = new Date().toISOString().slice(0, 7);
            const { data } = await api.get(`/hr/month-calc?month=${month}`);
            setPreview(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
    const toggleDay = (d) => {
        setWt((w) => {
            const has = w.weekly_holidays.includes(d);
            return { ...w, weekly_holidays: has ? w.weekly_holidays.filter((x) => x !== d) : [...w.weekly_holidays, d] };
        });
    };

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="wt-settings-card">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg">Work timings & overtime</div>
                <div className="text-xs text-stone-500">
                    Drives working days per month (for prorated salary) and OT rate = (base ÷ standard hours in month) × 1.5.
                </div>
            </div>
            <div className="p-5 space-y-4">
                <div className="grid grid-cols-4 gap-4">
                    <div><Label>Start time</Label>
                        <Input type="time" value={wt.start_time} onChange={(e) => setWt({ ...wt, start_time: e.target.value })} data-testid="wt-start" /></div>
                    <div><Label>End time</Label>
                        <Input type="time" value={wt.end_time} onChange={(e) => setWt({ ...wt, end_time: e.target.value })} data-testid="wt-end" /></div>
                    <div><Label>Lunch break (mins)</Label>
                        <Input type="number" value={wt.lunch_break_mins} onChange={(e) => setWt({ ...wt, lunch_break_mins: e.target.value })} data-testid="wt-lunch" /></div>
                    <div><Label>Tea breaks × mins each</Label>
                        <div className="flex gap-2">
                            <Input type="number" value={wt.tea_break_count} onChange={(e) => setWt({ ...wt, tea_break_count: e.target.value })} data-testid="wt-tea-count" />
                            <Input type="number" value={wt.tea_break_mins_each} onChange={(e) => setWt({ ...wt, tea_break_mins_each: e.target.value })} data-testid="wt-tea-mins" />
                        </div>
                    </div>
                </div>
                <div>
                    <Label className="text-[11px]">Weekly holidays</Label>
                    <div className="flex gap-2 mt-1" data-testid="wt-holidays">
                        {DAYS.map(([label, d]) => (
                            <button key={d} onClick={() => toggleDay(d)}
                                className="px-3 py-1 border rounded-sm text-xs"
                                style={{
                                    background: wt.weekly_holidays.includes(d) ? "#f59e0b" : "#fff",
                                    color: wt.weekly_holidays.includes(d) ? "#fff" : "#0f172a",
                                    borderColor: "var(--se-border)",
                                }}
                                data-testid={`wt-day-${d}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="text-[11px] p-3 bg-amber-50 rounded-sm border" style={{ borderColor: "var(--se-border)" }}>
                    <b>{hoursPerDay} hours/day</b> productive time
                    ({wt.start_time}–{wt.end_time}, minus {wt.lunch_break_mins} min lunch and {wt.tea_break_count} × {wt.tea_break_mins_each} min tea).
                    {preview ? <> · This month ({preview.month}): <b>{preview.working_days}</b> working days = <b>{preview.standard_hours}</b> standard hours.</> : null}
                </div>
                <div className="flex gap-2">
                    <Button onClick={save} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="wt-save">
                        <FloppyDisk size={14} className="mr-2" /> {saving ? "Saving…" : "Save timings"}
                    </Button>
                    <Button variant="outline" onClick={showThisMonth} data-testid="wt-preview">
                        Preview this month
                    </Button>
                </div>
            </div>
        </div>
    );
}



function PnLSettings() {
    const [startMonth, setStartMonth] = useState("2026-08");
    const [defaultStart, setDefaultStart] = useState("2026-08");
    const [candidates, setCandidates] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [configured, setConfigured] = useState(false);
    const [loading, setLoading] = useState(true);
    const [savingMonth, setSavingMonth] = useState(false);
    const [savingLedgers, setSavingLedgers] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [sm, dl] = await Promise.all([
                api.get("/settings/pnl-start-month"),
                api.get("/settings/pnl-direct-ledgers"),
            ]);
            setStartMonth(sm.data.month);
            setDefaultStart(sm.data.default);
            setCandidates(dl.data.candidates || []);
            setSelectedIds(dl.data.selected_ledger_ids || []);
            setConfigured(dl.data.explicitly_configured);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const saveMonth = async () => {
        if (!/^\d{4}-\d{2}$/.test(startMonth)) return toast.error("Month must be YYYY-MM");
        setSavingMonth(true);
        try {
            await api.put("/settings/pnl-start-month", { month: startMonth });
            toast.success("P&L start month saved");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingMonth(false); }
    };

    const toggleLedger = (id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    const saveLedgers = async () => {
        setSavingLedgers(true);
        try {
            await api.put("/settings/pnl-direct-ledgers", { ledger_ids: selectedIds });
            toast.success("Direct-purchase ledgers saved");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingLedgers(false); }
    };

    // Group candidates by (category, group_name) for a clean UI
    const groupedCats = { direct_cost: {}, indirect_expenses: {} };
    candidates.forEach((l) => {
        const cat = l.category || "indirect_expenses";
        groupedCats[cat] = groupedCats[cat] || {};
        groupedCats[cat][l.group_name] = groupedCats[cat][l.group_name] || [];
        groupedCats[cat][l.group_name].push(l);
    });

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="pnl-settings-card">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg">Monthly P&amp;L</div>
                <div className="text-xs text-stone-500">
                    Configure when the P&amp;L starts and which ledgers count as <b>Direct Purchases</b> for COGS.
                </div>
            </div>
            <div className="p-5 space-y-6">
                <div className="grid gap-4 md:grid-cols-2 items-end">
                    <div>
                        <Label>P&amp;L start month</Label>
                        <Input type="month" value={startMonth} onChange={(e) => setStartMonth(e.target.value)}
                               disabled={loading || savingMonth} data-testid="pnl-start-month-input" />
                        <div className="text-[11px] text-stone-500 mt-1">
                            Report is unavailable for any month before this. Default: <b>{defaultStart}</b>.
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={saveMonth} disabled={savingMonth}
                                style={{ background: "#0d6efd", color: "#fff" }} data-testid="pnl-start-month-save">
                            <FloppyDisk size={14} className="mr-2" /> {savingMonth ? "Saving…" : "Save start month"}
                        </Button>
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <div className="text-sm font-medium">Direct-purchase ledgers ({selectedIds.length} selected)</div>
                            <div className="text-[11px] text-stone-500">
                                Ticked ledgers are treated as feeding COGS: <b>Opening + these purchases − Closing = COGS</b>.
                                {!configured ? <> Currently using <b>auto-defaults</b> (Raw Material, Inward Freight, Consumables, Direct Labour).</> : null}
                            </div>
                        </div>
                        <Button onClick={saveLedgers} disabled={savingLedgers}
                                style={{ background: "#0d6efd", color: "#fff" }} data-testid="pnl-direct-save">
                            <FloppyDisk size={14} className="mr-2" /> {savingLedgers ? "Saving…" : "Save selection"}
                        </Button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <CategoryBlock label="Direct cost ledgers" tone="#dc2626"
                                        groups={groupedCats.direct_cost} selectedIds={selectedIds}
                                        toggle={toggleLedger} testid="pnl-direct-block" />
                        <CategoryBlock label="Indirect expense ledgers"
                                        tone="#f97316" groups={groupedCats.indirect_expenses}
                                        selectedIds={selectedIds} toggle={toggleLedger}
                                        testid="pnl-indirect-block"
                                        note="These are indirect by default; tick only the ones you count as 'production labour' or similar." />
                    </div>
                </div>

                <TDSPartiesCard />
            </div>
        </div>
    );
}

function CategoryBlock({ label, tone, groups, selectedIds, toggle, testid, note }) {
    const groupNames = Object.keys(groups || {}).sort();
    return (
        <div className="border rounded-sm" style={{ borderColor: "var(--se-border)" }} data-testid={testid}>
            <div className="px-3 py-2 border-b text-xs uppercase tracking-widest"
                 style={{ borderColor: "var(--se-border)", color: tone, background: "#fafaf9" }}>
                {label}
            </div>
            {note ? <div className="px-3 pt-2 text-[10px] text-stone-500">{note}</div> : null}
            <div className="p-3 space-y-3 max-h-80 overflow-y-auto">
                {groupNames.length === 0 ? (
                    <div className="text-xs text-stone-500">No ledgers</div>
                ) : groupNames.map((g) => (
                    <div key={g}>
                        <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">{g}</div>
                        <div className="space-y-1">
                            {groups[g].map((l) => (
                                <label key={l.id}
                                       className="flex items-start gap-2 text-sm cursor-pointer hover:bg-stone-50 rounded-sm px-1 py-0.5"
                                       data-testid={`pnl-ledger-checkbox-${l.id}`}>
                                    <input type="checkbox" checked={selectedIds.includes(l.id)}
                                           onChange={() => toggle(l.id)} className="mt-1" />
                                    <span>{l.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function TDSPartiesCard() {
    const [rows, setRows] = useState([]);
    const [showInactive, setShowInactive] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/tds-parties", { params: { active_only: !showInactive } });
            setRows(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, [showInactive]);

    const del = async (p) => {
        if (!confirm(`Deactivate ${p.name}? (History stays intact.)`)) return;
        try { await api.delete(`/tds-parties/${p.id}`); toast.success("Deactivated"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid="tds-parties-card">
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                <div>
                    <div className="font-serif text-lg">TDS parties</div>
                    <div className="text-xs text-stone-500">Landlords &amp; contractors on whom TDS is deducted at source. Adding a party auto-creates a dedicated expense ledger so history stays clean per PAN.</div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-[11px] flex items-center gap-1">
                        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="tds-show-inactive" />
                        Show inactive
                    </label>
                    <Button onClick={() => { setEditing(null); setDialogOpen(true); }} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="tds-new">New party</Button>
                </div>
            </div>
            <table className="se-table" data-testid="tds-parties-table">
                <thead>
                    <tr><th>Name</th><th>PAN</th><th>Section</th><th>Type</th><th className="text-right">Rate</th><th>Ledger</th><th></th></tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan="7" className="text-center py-8 text-sm text-stone-500">
                            No TDS parties configured. Add landlords (194I @ 10%) or contractors (194C @ 1%/2%) here.
                        </td></tr>
                    ) : rows.map((p) => (
                        <tr key={p.id} data-testid={`tds-row-${p.id}`}>
                            <td>{p.name}{!p.active ? <span className="text-[10px] text-stone-500 ml-2">(inactive)</span> : null}</td>
                            <td className="font-mono text-xs">{p.pan}</td>
                            <td className="text-xs">{p.section}</td>
                            <td className="text-xs capitalize">{p.party_type}</td>
                            <td className="text-right se-num">{p.rate_pct}%</td>
                            <td className="text-xs text-stone-600">{p.expense_ledger_name || "—"}</td>
                            <td className="text-right">
                                <Button size="sm" variant="outline" onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid={`tds-edit-${p.id}`} className="mr-1">Edit</Button>
                                {p.active ? <Button size="sm" variant="outline" onClick={() => del(p)} data-testid={`tds-del-${p.id}`}>Off</Button> : null}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <TDSPartyDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={load} />
        </div>
    );
}

function TDSPartyDialog({ open, onOpenChange, editing, onSaved }) {
    const [form, setForm] = useState({ name: "", pan: "", section: "194I", party_type: "individual", notes: "" });
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (open) {
            setForm(editing || { name: "", pan: "", section: "194I", party_type: "individual", notes: "" });
        }
    }, [open, editing]);

    const rate = form.section === "194I" ? 10 : (form.party_type === "individual" ? 1 : 2);

    const save = async () => {
        if (!form.name?.trim()) return toast.error("Name required");
        if (!/^[A-Z]{5}\d{4}[A-Z]$/.test((form.pan || "").toUpperCase())) return toast.error("PAN must be ABCDE1234F format");
        setSaving(true);
        try {
            const body = { name: form.name.trim(), pan: form.pan.toUpperCase(), section: form.section, party_type: form.party_type, notes: form.notes };
            if (editing) await api.patch(`/tds-parties/${editing.id}`, body);
            else await api.post("/tds-parties", body);
            toast.success(editing ? "Updated" : "Added");
            onOpenChange(false); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="tds-dialog">
                <DialogHeader><DialogTitle>{editing ? "Edit TDS party" : "New TDS party"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div><Label>Name *</Label><Input value={form.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="tds-form-name" /></div>
                    <div><Label>PAN * (ABCDE1234F)</Label><Input value={form.pan || ""} onChange={(e) => setForm((f) => ({ ...f, pan: e.target.value.toUpperCase() }))} data-testid="tds-form-pan" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Section *</Label>
                            <select className="w-full border rounded-sm px-3 py-2 text-sm" value={form.section} onChange={(e) => setForm((f) => ({ ...f, section: e.target.value }))} data-testid="tds-form-section">
                                <option value="194I">194I — Rent</option>
                                <option value="194C">194C — Contract</option>
                            </select></div>
                        <div><Label>Party type *</Label>
                            <select className="w-full border rounded-sm px-3 py-2 text-sm" value={form.party_type} onChange={(e) => setForm((f) => ({ ...f, party_type: e.target.value }))} data-testid="tds-form-type">
                                <option value="individual">Individual</option>
                                <option value="company">Company / firm / HUF</option>
                            </select></div>
                    </div>
                    <div className="text-xs text-stone-600 border rounded-md p-2" style={{ borderColor: "var(--se-border)", background: "#fefce8" }}>
                        Deduction rate: <strong>{rate}%</strong> · TDS credited to <strong>TDS Payable ({form.section === "194I" ? "Rent 194I @ 10%" : `Contract 194C @ ${rate}%`})</strong>.
                    </div>
                    <div><Label>Notes</Label><textarea className="w-full border rounded-sm px-3 py-2 text-sm min-h-14" value={form.notes || ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={save} disabled={saving} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="tds-form-save">{saving ? "Saving..." : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ---------------------------- Unit Economics settings ---------------------------- */
function UnitEconomicsSettings() {
    const [planned, setPlanned] = useState({
        selling_price_incl_tax: "",
        taxable_revenue: "",
        cogs_and_labour: "",
        operating_costs: "",
        sales_and_marketing: "",
    });
    const [buckets, setBuckets] = useState({ operating_ledger_ids: [], sm_ledger_ids: [] });
    const [candidates, setCandidates] = useState([]);
    const [savingPlanned, setSavingPlanned] = useState(false);
    const [savingBuckets, setSavingBuckets] = useState(false);
    const [q, setQ] = useState("");

    const load = async () => {
        try {
            const [p, b] = await Promise.all([
                api.get("/settings/unit-economics-planned"),
                api.get("/settings/unit-economics-buckets"),
            ]);
            setPlanned({
                selling_price_incl_tax: String(p.data.selling_price_incl_tax || ""),
                taxable_revenue:        String(p.data.taxable_revenue || ""),
                cogs_and_labour:        String(p.data.cogs_and_labour || ""),
                operating_costs:        String(p.data.operating_costs || ""),
                sales_and_marketing:    String(p.data.sales_and_marketing || ""),
            });
            setBuckets({
                operating_ledger_ids: b.data.operating_ledger_ids || [],
                sm_ledger_ids:        b.data.sm_ledger_ids || [],
            });
            setCandidates(b.data.candidates || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const gross = Math.max(0, (Number(planned.taxable_revenue) || 0) - (Number(planned.cogs_and_labour) || 0));
    const cm2   = Math.max(0, gross - (Number(planned.operating_costs) || 0) - (Number(planned.sales_and_marketing) || 0));

    const savePlanned = async () => {
        setSavingPlanned(true);
        try {
            await api.put("/settings/unit-economics-planned", {
                selling_price_incl_tax: Number(planned.selling_price_incl_tax) || 0,
                taxable_revenue:        Number(planned.taxable_revenue) || 0,
                cogs_and_labour:        Number(planned.cogs_and_labour) || 0,
                operating_costs:        Number(planned.operating_costs) || 0,
                sales_and_marketing:    Number(planned.sales_and_marketing) || 0,
            });
            toast.success("Planned unit economics saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingPlanned(false); }
    };

    const saveBuckets = async () => {
        setSavingBuckets(true);
        try {
            await api.put("/settings/unit-economics-buckets", buckets);
            toast.success("Ledger buckets saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingBuckets(false); }
    };

    const toggle = (bucket, id) => {
        setBuckets((b) => {
            const other = bucket === "operating_ledger_ids" ? "sm_ledger_ids" : "operating_ledger_ids";
            const has = b[bucket].includes(id);
            return {
                ...b,
                [bucket]: has ? b[bucket].filter((x) => x !== id) : [...b[bucket], id],
                // Auto-remove from the OTHER bucket to prevent overlap
                [other]:  b[other].filter((x) => x !== id),
            };
        });
    };

    const filtered = candidates.filter((c) =>
        !q || c.name.toLowerCase().includes(q.toLowerCase())
              || (c.group_name || "").toLowerCase().includes(q.toLowerCase())
    );
    // Group by group_name for a scan-friendly picker
    const grouped = filtered.reduce((acc, l) => {
        (acc[l.group_name] ||= []).push(l);
        return acc;
    }, {});

    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}
             data-testid="ue-settings-card">
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg">Unit economics</div>
                <div className="text-xs text-stone-500">
                    Planned per-unit targets (assumptions) + which ledgers feed each bucket in the Monthly P&L → Unit economics view.
                </div>
            </div>

            {/* Planned assumptions */}
            <div className="p-5 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-medium">Planned assumptions <span className="text-stone-400 text-xs">(₹ per unit)</span></div>
                    <Button onClick={savePlanned} disabled={savingPlanned}
                            style={{ background: "#0d6efd", color: "#fff" }}
                            data-testid="ue-save-planned">
                        <FloppyDisk size={13} className="mr-1" /> {savingPlanned ? "Saving…" : "Save assumptions"}
                    </Button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    {[
                        { k: "selling_price_incl_tax", label: "Selling price (incl. tax)" },
                        { k: "taxable_revenue",        label: "Taxable revenue" },
                        { k: "cogs_and_labour",        label: "COGS + Labour" },
                        { k: "operating_costs",        label: "Operating costs" },
                        { k: "sales_and_marketing",    label: "Sales & marketing" },
                    ].map((f) => (
                        <div key={f.k}>
                            <Label className="text-[10px] uppercase tracking-widest">{f.label}</Label>
                            <Input type="number" min="0"
                                   value={planned[f.k]}
                                   onChange={(e) => setPlanned({ ...planned, [f.k]: e.target.value })}
                                   placeholder="0"
                                   className="h-9 text-sm"
                                   data-testid={`ue-planned-${f.k}`} />
                        </div>
                    ))}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                    <div className="rounded-md px-3 py-2"
                         style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534" }}
                         data-testid="ue-planned-gross">
                        <b>Gross margin:</b> ₹{inr(gross)}
                        {Number(planned.taxable_revenue) > 0 ? (
                            <span className="ml-1 text-stone-500">
                                ({((gross / Number(planned.taxable_revenue)) * 100).toFixed(1)}% of taxable)
                            </span>
                        ) : null}
                    </div>
                    <div className="rounded-md px-3 py-2"
                         style={{ background: "#faf5ff", border: "1px solid #d8b4fe", color: "#4c1d95" }}
                         data-testid="ue-planned-cm2">
                        <b>CM2 (Contribution margin):</b> ₹{inr(cm2)}
                        {Number(planned.taxable_revenue) > 0 ? (
                            <span className="ml-1 text-stone-500">
                                ({((cm2 / Number(planned.taxable_revenue)) * 100).toFixed(1)}% of taxable)
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Ledger buckets */}
            <div className="p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                        <div className="text-sm font-medium">Ledger to bucket mapping</div>
                        <div className="text-[11px] text-stone-500">
                            <b>Operating</b> covers Transport + Txn + Warranty.
                            <b> Sales & marketing</b> covers CAC + Commission.
                            COGS is inherited from the P&amp;L Direct-purchase ledgers above.
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Input placeholder="Search ledger / group…" value={q} onChange={(e) => setQ(e.target.value)}
                               className="w-56 h-8 text-xs" data-testid="ue-bucket-search" />
                        <Button onClick={saveBuckets} disabled={savingBuckets}
                                style={{ background: "#0d6efd", color: "#fff" }}
                                data-testid="ue-save-buckets">
                            <FloppyDisk size={13} className="mr-1" /> {savingBuckets ? "Saving…" : "Save buckets"}
                        </Button>
                    </div>
                </div>
                <div className="border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="w-full text-sm">
                        <thead>
                            <tr style={{ background: "#fafaf9", borderBottom: "1px solid var(--se-border)" }}>
                                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-stone-500">Ledger</th>
                                <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest text-stone-500 w-32">Operating</th>
                                <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest text-stone-500 w-40">Sales & marketing</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(grouped).map(([g, rows]) => (
                                <>
                                    <tr key={`h-${g}`}>
                                        <td colSpan={3} className="px-3 py-1 text-[10px] uppercase tracking-widest text-stone-500"
                                            style={{ background: "#f5f5f4" }}>
                                            {g}
                                        </td>
                                    </tr>
                                    {rows.map((l) => (
                                        <tr key={l.id} className="border-t" style={{ borderColor: "var(--se-border)" }}
                                            data-testid={`ue-row-${l.id}`}>
                                            <td className="px-3 py-1.5">{l.name}</td>
                                            <td className="px-3 py-1.5 text-center">
                                                <input type="checkbox"
                                                       checked={buckets.operating_ledger_ids.includes(l.id)}
                                                       onChange={() => toggle("operating_ledger_ids", l.id)}
                                                       data-testid={`ue-op-${l.id}`} />
                                            </td>
                                            <td className="px-3 py-1.5 text-center">
                                                <input type="checkbox"
                                                       checked={buckets.sm_ledger_ids.includes(l.id)}
                                                       onChange={() => toggle("sm_ledger_ids", l.id)}
                                                       data-testid={`ue-sm-${l.id}`} />
                                            </td>
                                        </tr>
                                    ))}
                                </>
                            ))}
                            {filtered.length === 0 ? (
                                <tr><td colSpan={3} className="px-3 py-6 text-center text-xs text-stone-400">No ledger matches your search.</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
                <div className="mt-2 text-[11px] text-stone-500">
                    Ticking Operating auto-removes the ledger from Sales & marketing (and vice-versa) — a ledger can be in one bucket at most.
                </div>
            </div>
        </div>
    );
}

